import React, { useContext, useState, useEffect } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { removeStorageKey, setStorageKey } from "../core/utils/storage";
import { SettingsContext } from '../contexts/index.jsx';
import { Loading } from "../components";
import StatePanel from "../components/ui/StatePanel";
import { requireResponse } from "../core/errors/requireResponse";
import { useListing } from "../listings/hooks/useListing";
import {
  CarouselGallery,
  Details,
  InternalDetails,
  ListingPreviewBanner,
  PublishSuccessModal,
  RCProductInsights,
} from "./Listing/index.jsx";
import PageHeader from "../components/layout/PageHeader";
import PageLayout from "../components/layout/PageLayout";
import { Icon, IconButton } from "../components/ui";
import Search from "./Search";
import "./Listing/rc-product-insights.scss";
import "./Listing/rc-product-theme.scss";
import "./Listing/internal-details.scss";

const saveProductReferralCode = (referralCode) => {
  if (!referralCode) {
    return removeStorageKey("productReferralCode");
  }

  setStorageKey("productReferralCode", referralCode);
};

const Listing = ({ user }) => {
  const settings = useContext(SettingsContext);
  const location = useLocation();
  const history = useHistory();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { listing, isLoading, isError, refetch } = useListing(id);
  const [publishStatus, setPublishStatus] = useState();

  useEffect(() => {
    const productReferralCode = location.search?.split("ref=")?.[1];
    saveProductReferralCode(productReferralCode);

    const hexId = parseInt(id, 10)
      .toString(16)
      .toUpperCase();
    if (
      (!user || user.status === "limited_to_referred_product") &&
      productReferralCode?.indexOf(`-${hexId}-`) === -1
    ) {
      // The LIMITED users should access only the products referred to them.
      // The referral code includes the id of the product and we use that to verify the access.
      // This verification should be enough for now; The level of security needed here
      // is not high enough to justify calling an API to validate the referral code.
      history.replace("/error/403");
    }
  }, [id, location.search, user, history]);

  // Signed-out visitors (clients from a live lookbook) have no theme toggle,
  // and this page is dark-first: under an OS light preference the tokens went
  // light and the title rendered dark-on-dark. Pin dark while it's mounted;
  // put back whatever was there on the way out.
  useEffect(() => {
    if (user) return undefined;
    const root = document.documentElement;
    const previous = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "dark");
    return () => (previous ? root.setAttribute("data-theme", previous) : root.removeAttribute("data-theme"));
  }, [user]);

  // A failed load used to leave the spinner up forever.
  if (!listing && isError) {
    return (
      <StatePanel
        state="error"
        title="Couldn't load this listing"
        description="Something went wrong on our side — try again."
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !listing) {
    return <Loading type="loading" />;
  }

  const onPublishClick = async () => {
    setPublishStatus("submitting");

    try {
      // A 5xx resolves undefined (global interceptor): without the guard the
      // page said "published" and cached status: active for a listing that
      // never went live.
      requireResponse(
        await axios.put(`/api/listings/${listing.id}/active`, {
          active: true,
        })
      );

      queryClient.setQueryData(["listing", String(listing.id)], {
        ...listing,
        status: "active",
      });
      setPublishStatus("published");
    } catch (error) {
      setPublishStatus("error");
    }
  };

  const backUrl = `/listings?${settings.iframeQueryParams}`;

  // Product display layout follows DAO Watches product page logic:
  // gallery + details side-by-side (d-xl-flex), then internal details.
  return (
    <PageLayout id="product" className="flex-grow-1">
      {/* Signed-out visitors (clients opening a listing from a live lookbook)
          get a clean read-only page: no staff title band, no product search. */}
      {user && (
        <>
          {/* Every product page carries the same page title as the rest of the app. */}
          <PageHeader title="Product Database" />

          {/* Was rendered by the legacy Default header, which sat ABOVE the title on
              its own band. Here it reads title → controls → content, like /rc/listings. */}
          <Search withFilters />
        </>
      )}

      {settings.isMerchant && (
        <div className={`home-button view-listing`}>
          <span className="home-merchant-link pr-15">
            <IconButton variant="ghost" label="Back to listings" href={backUrl} data-btn="listings-open">
              <Icon name="Home" size="sm" />
            </IconButton>
          </span>
        </div>
      )}

      {listing.status !== "active" &&
        user?.is_admin &&
        user?.id == listing.user_id && (
          <ListingPreviewBanner
            listing={listing}
            iframeQueryParams={settings.iframeQueryParams}
            publishStatus={publishStatus}
            onPublish={onPublishClick}
          />
        )}

      {/* Publish confirmation is a modal, not an inline banner — the banner
          rendered above the fold on mobile and pushed the listing down. */}
      <PublishSuccessModal
        isOpen={publishStatus === "published"}
        onClose={() => setPublishStatus()}
        backUrl={backUrl}
      />

      <div className="d-xl-flex">
        <CarouselGallery
          listing={listing}
          images={listing.product.images}
          extraImages={listing?.images || []}
          className="flex-grow-1"
        />

        <Details {...{ user, listing }} />
      </div>

      <InternalDetails {...{ listing }} />

      <RCProductInsights
        user={user}
        product={listing.product}
        requestType={null}
      />
    </PageLayout>
  );
};

export default Listing;
